import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Mail, Send, Plus, Trash2, Lock, RefreshCw, RotateCcw, Reply, ChevronDown, ChevronRight, Clock, Settings, MailPlus } from 'lucide-react';
import { useAdmin } from './AdminHooks';
import { adminAPI } from './AdminUtils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../ui/table';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { useToast } from '../../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { getEmailContentTemplates } from './email/EmailContentTemplates';

const ModernNotificationsTab = () => {
  const { isAuthenticated, setEmailsUnread, composeIntent, setComposeIntent, setEmailsQueued, clients, nextEvent } = useAdmin();
  const { toast } = useToast();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newForward, setNewForward] = useState('');
  const [smtpStatus, setSmtpStatus] = useState({ smtpConfigured: false });
  const [outbox, setOutbox] = useState({ items: [], total: 0 });
  const [sent, setSent] = useState({ items: [], total: 0 });
  const [sentQuery, setSentQuery] = useState('');
  const [sentSort, setSentSort] = useState('-createdAt');
  const [received, setReceived] = useState({ items: [], total: 0, resendConfigured: false });
  const [receivedQuery, setReceivedQuery] = useState('');
  const [receivedSort, setReceivedSort] = useState('-created_at');
  const [receivedPrefix, setReceivedPrefix] = useState('');
  const [receivedFolder, setReceivedFolder] = useState('inbox');
  const [composePrefix, setComposePrefix] = useState('');
  const [composeDisplayName, setComposeDisplayName] = useState('');
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeHtml, setComposeHtml] = useState('');
  const [composeReplyTo, setComposeReplyTo] = useState('');
  const [composeCc, setComposeCc] = useState('');
  const [composeBcc, setComposeBcc] = useState('');
  const [composeSending, setComposeSending] = useState(false);
  const [composeUseTemplate, setComposeUseTemplate] = useState(true);
  const [composeFormat, setComposeFormat] = useState('text');
  const [replyFormat, setReplyFormat] = useState('text');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyContext, setReplyContext] = useState(null);
  const [mailTab, setMailTab] = useState('received');
  const [composeOpen, setComposeOpen] = useState(false);
  const [receivedOpen, setReceivedOpen] = useState(true);
  const senderDomain = 'crapule.xyz';
  const [allReceived, setAllReceived] = useState({ items: [], total: 0 });
  const [metrics, setMetrics] = useState({ unreadTotal: 0, perPrefixUnread: {}, resendConfigured: true });
  const [refreshing, setRefreshing] = useState(false);
  const [composeGroup, setComposeGroup] = useState('custom');
  const [composeTemplateId, setComposeTemplateId] = useState('');
  const [replyTemplateId, setReplyTemplateId] = useState('');
  const contentTemplates = React.useMemo(() => getEmailContentTemplates(nextEvent), [nextEvent]);
  const [templatesState, setTemplatesState] = useState(() => {
    try {
      const list = getEmailContentTemplates(nextEvent);
      return list.map(t => ({ id: t.id, label: t.label, html: t.render(nextEvent) }));
    } catch {
      return [];
    }
  });
  const [editTplOpen, setEditTplOpen] = useState(false);
  const [editTplIndex, setEditTplIndex] = useState(-1);
  const [editTplLabel, setEditTplLabel] = useState('');
  const [editTplHtml, setEditTplHtml] = useState('');
  const [editModelOpen, setEditModelOpen] = useState(false);
  const [editModelIndex, setEditModelIndex] = useState(-1);
  const [editModelData, setEditModelData] = useState({
    name: '',
    enabled: true,
    trigger: { type: 'reservation_status', toStatus: 'pending', mode: 'on_change', prefix: '' },
    subjectTemplate: '',
    bodyTemplate: '',
  });
  const openEditModel = (idx) => {
    const m = (cfg?.models || [])[idx];
    if (!m) return;
    setEditModelIndex(idx);
    setEditModelData({
      name: m.name || '',
      enabled: !!m.enabled,
      trigger: { type: m?.trigger?.type || 'reservation_status', toStatus: m?.trigger?.toStatus || 'pending', mode: m?.trigger?.mode || 'on_change', prefix: m?.trigger?.prefix || '' },
      subjectTemplate: m.subjectTemplate || '',
      bodyTemplate: m.bodyTemplate || '',
    });
    setEditModelOpen(true);
  };
  const closeEditModel = () => {
    setEditModelOpen(false);
    setEditModelIndex(-1);
    setEditModelData({ name: '', enabled: true, trigger: { type: 'reservation_status', toStatus: 'pending', mode: 'on_change', prefix: '' }, subjectTemplate: '', bodyTemplate: '' });
  };
  const saveEditModel = async () => {
    const idx = editModelIndex;
    if (idx < 0) return;
    const next = { ...(cfg || {}) };
    next.models = [...(cfg?.models || [])];
    next.models[idx] = {
      ...(next.models[idx] || {}),
      name: editModelData.name,
      enabled: !!editModelData.enabled,
      trigger: { type: editModelData.trigger.type, mode: editModelData.trigger.mode || 'on_change', toStatus: editModelData.trigger.toStatus, prefix: editModelData.trigger.prefix || '' },
      subjectTemplate: editModelData.subjectTemplate,
      bodyTemplate: editModelData.bodyTemplate,
    };
    await updateAndSave(next);
    closeEditModel();
  };
  const toSerializableTemplates = (list) => {
    try {
      return (list || []).map(t => ({
        id: String(t.id || `tpl-${Date.now()}`),
        label: String(t.label || 'Modèle'),
        html: String(t.html || ''),
      }));
    } catch {
      return [];
    }
  };
  const openEditTemplate = (idx) => {
    try {
      const t = templatesState[idx];
      setEditTplIndex(idx);
      setEditTplLabel(t?.label || '');
      setEditTplHtml(t?.html || '');
      setEditTplOpen(true);
    } catch {
      setEditTplIndex(-1);
      setEditTplLabel('');
      setEditTplHtml('');
      setEditTplOpen(true);
    }
  };
  const closeEditTemplate = () => {
    setEditTplOpen(false);
    setEditTplIndex(-1);
    setEditTplLabel('');
    setEditTplHtml('');
  };
  const saveEditTemplate = () => {
    if (editTplIndex < 0) return;
    setTemplatesState(prev => {
      const next = [...prev];
      next[editTplIndex] = { ...next[editTplIndex], label: editTplLabel, html: editTplHtml };
      return next;
    });
    const nextTemplates = toSerializableTemplates(
      templatesState.map((t, i) => (i === editTplIndex ? { ...t, label: editTplLabel, html: editTplHtml } : t))
    );
    const nextCfg = { ...(cfg || {}), contentTemplates: nextTemplates };
    updateAndSave(nextCfg);
    closeEditTemplate();
  };
  const addNewTemplate = () => {
    const id = `custom-${Date.now()}`;
    const nextList = [...templatesState, { id, label: 'Nouveau modèle', html: '' }];
    setTemplatesState(nextList);
    const nextCfg = { ...(cfg || {}), contentTemplates: toSerializableTemplates(nextList) };
    updateAndSave(nextCfg);
  };
  const removeTemplate = (idx) => {
    const nextList = templatesState.filter((_, i) => i !== idx);
    setTemplatesState(nextList);
    const nextCfg = { ...(cfg || {}), contentTemplates: toSerializableTemplates(nextList) };
    updateAndSave(nextCfg);
  };
  const applyLocalTemplate = (id) => {
    const t = templatesState.find(x => x.id === id);
    return t ? t.html : '';
  };
  const [modelsTab, setModelsTab] = useState('none');
  const [settingsTab, setSettingsTab] = useState('general');
  const prefixOptions = React.useMemo(() => {
    try {
      const items = allReceived?.items || [];
      const all = items.flatMap(it => Array.isArray(it.prefixes) ? it.prefixes : []);
      return Array.from(new Set(all)).sort((a, b) => String(a).localeCompare(String(b), 'fr', { sensitivity: 'base' }));
    } catch {
      return [];
    }
  }, [allReceived?.items]);
  const prefixStats = React.useMemo(() => {
    const m = metrics?.perPrefixUnread || {};
    const res = {};
    Object.keys(m).forEach(k => { res[k] = { unread: m[k]?.unread || 0 }; });
    return res;
  }, [metrics]);
  const allStats = React.useMemo(() => {
    return { total: 0, unread: metrics?.unreadTotal || 0 };
  }, [metrics]);
  const sanitizeForPreview = (html) => {
    try {
      const t = String(html || '');
      const noScripts = t.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
      const noEvents = noScripts.replace(/\son\w+="[^"]*"/gi, '').replace(/\son\w+='[^']*'/gi, '');
      return noEvents;
    } catch {
      return String(html || '');
    }
  };
  const escapeHtml = (s) => String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const formatDateTime = (s) => {
    if (!s) return '';
    const d = new Date(s);
    if (isNaN(d)) return String(s);
    return d.toLocaleString('fr-BE', {
      timeZone: 'Europe/Brussels',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };
  const extractEmail = (s) => {
    if (!s) return '';
    const m = String(s).match(/<([^>]+)>/);
    return (m && m[1]) ? m[1].trim().toLowerCase() : String(s).trim().toLowerCase();
  };
  const extractPrefix = (s) => {
    const email = extractEmail(s);
    if (email.includes('@')) {
      const [local, dom] = email.split('@');
      if (dom === senderDomain) return local;
    }
    return '';
  };
  const [sentPage, setSentPage] = useState(1);
  const [sentPageSize, setSentPageSize] = useState(10);
  const [receivedPage, setReceivedPage] = useState(1);
  const [receivedPageSize, setReceivedPageSize] = useState(10);
  const [outboxEdits, setOutboxEdits] = useState({});
  const [sentEdits, setSentEdits] = useState({});
  const getRecipientsString = (m) => {
    try { return Array.isArray(m.recipients) ? m.recipients.join(', ') : (m.recipients || '').toString(); } catch { return ''; }
  };
  const resendQueuedEmail = async (m) => {
    try {
      const toStr = outboxEdits[m.id] ?? getRecipientsString(m);
      const to = (toStr || '').split(',').map(s => s.trim()).filter(Boolean);
      const subject = m.subject || '';
      const html = m.body || '';
      if (to.length === 0) return;
      await adminAPI.post('/notifications/compose/send', { to, subject, html });
      await adminAPI.delete(`/notifications/outbox/${m.id}`);
      const ob = await adminAPI.get('/notifications/outbox');
      setOutbox(ob.data);
      const se = await adminAPI.get('/notifications/sent', { params: { q: sentQuery, sort: sentSort } });
      setSent(se.data);
    } catch {}
  };
  const testModelSend = async (idx) => {
    const m = (cfg?.models || [])[idx];
    if (!m) return;
    const sampleCtx = {
      volume: String(nextEvent?.volume || ''),
      theme: String(nextEvent?.theme || ''),
      name: 'Client',
      guests: 2,
      price: Number(nextEvent?.price || 0),
      date: String(nextEvent?.date || ''),
      time: String(nextEvent?.time || ''),
      status: (m?.trigger?.toStatus || ''),
      table: 'A1',
      orderId: 'ORDER-XXX',
      subject: 'Sujet reçu',
      from: 'expediteur@example.com',
      to: 'prefix@crapule.xyz',
    };
    const renderPreview = (tpl) => {
      try { return String(tpl || '').replace(/\{(\w+)\}/g, (_, k) => String(sampleCtx[k] ?? '')); } catch { return String(tpl || ''); }
    };
    const recipients = (cfg?.additionalEmails || []).filter(Boolean);
    if (recipients.length === 0) {
      toast({ title: 'Aucun destinataire', description: 'Ajoutez des Destinataires additionnels pour tester.', variant: 'destructive' });
      return;
    }
    setTesting(true);
    try {
      const subject = renderPreview(m.subjectTemplate || 'Aperçu');
      const html = buildEmailHtml(renderPreview(m.bodyTemplate || ''), subject);
      await adminAPI.post('/notifications/compose/send', { prefix: 'test', displayName: 'Notifications', to: recipients, subject, html });
      toast({ title: 'Test envoyé', description: `Modèle: ${m.name || ''}` });
    } finally {
      setTesting(false);
    }
  };
  useEffect(() => {
    try {
      const total = (cfg?.models || []).length;
      if (total > 0) {
        const first = `model-0`;
        if (!modelsTab || modelsTab === 'none') {
          setModelsTab(first);
        }
      } else {
        if (modelsTab !== 'none') setModelsTab('none');
      }
    } catch {}
  }, [cfg?.models]);
  const resendSentEmail = async (m) => {
    try {
      const toStr = sentEdits[m.id] ?? getRecipientsString(m);
      const to = (toStr || '').split(',').map(s => s.trim()).filter(Boolean);
      const subject = m.subject || '';
      const html = m.body || '';
      if (to.length === 0) return;
      await adminAPI.post('/notifications/compose/send', { to, subject, html });
      const se = await adminAPI.get('/notifications/sent', { params: { q: sentQuery, sort: sentSort } });
      setSent(se.data);
    } catch {}
  };
  useEffect(() => {
    if (composeIntent && composeIntent.open) {
      setComposeTo(composeIntent.to || '');
      setComposeSubject(composeIntent.subject || '');
      setComposeOpen(true);
      setReplyOpen(false);
      setComposeIntent({ to: '', subject: '', open: false });
    }
  }, [composeIntent && composeIntent.open]);
  const resetComposeFields = () => {
    setComposePrefix('');
    setComposeDisplayName('');
    setComposeTo('');
    setComposeSubject('');
    setComposeHtml('');
    setComposeReplyTo('');
    setComposeCc('');
    setComposeBcc('');
    setComposeUseTemplate(true);
    setComposeGroup('custom');
    setComposeFormat('text');
  };
  const resetReplyFields = () => {
    setComposeTo('');
    setComposeSubject('');
    setComposeReplyTo('');
    setComposeCc('');
    setComposeBcc('');
    setComposeHtml('');
    setComposeUseTemplate(true);
    setReplyContext(null);
    setReplyFormat('text');
  };
  const buildEmailHtml = (contentHTML, subject) => {
    const safeContent = contentHTML || '<p style="color:#e5e7eb;">(Sans contenu)</p>';
    const safeSubject = subject || 'Aperçu';
    return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8" />
  <title>${safeSubject}</title>
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    .preheader { display:none !important; visibility:hidden; opacity:0; color:transparent; height:0; width:0; overflow:hidden; }
    @media only screen and (max-width:600px) {
      .container { width:100% !important; }
      .p-30 { padding:20px !important; }
      .p-28 { padding:20px !important; }
      .p-22 { padding:16px !important; }
      .h1 { font-size:20px !important; }
      .logo { font-size:24px !important; }
      .section { margin:14px 0 !important; }
    }
  </style>
</head>
<body style="margin:0; padding:0; background-color:#0b0b0d; font-family: Arial, Helvetica, sans-serif;">
  <div class="preheader">${safeSubject}</div>
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#0b0b0d; padding:30px 0;">
    <tr>
      <td align="center">
        <table role="presentation" class="container" width="100%" cellpadding="0" cellspacing="0" style="width:100%; max-width:600px; background-color:#111318; border-radius:12px; overflow:hidden; box-shadow:0 6px 20px rgba(0,0,0,0.35); margin:0 auto;">
          <tr>
            <td class="p-28" style="background-color:#000000; color:#ffffff; padding:28px 24px; text-align:center; border-bottom:1px solid #1a1a1a;">
              <div style="display:inline-block; text-align:center; line-height:1;">
                <div class="logo" style="font-size:28px; font-weight:900; letter-spacing:-0.5px; color:#ffffff;">CRA</div>
                <div class="logo" style="font-size:28px; font-weight:900; letter-spacing:-0.5px; color:#ffffff;">PULE</div>
              </div>
            </td>
          </tr>
          <tr>
            <td class="p-30" style="padding:30px; color:#e5e7eb; font-size:15px; line-height:1.7;">
              <h1 class="h1" style="margin:0 0 8px 0; font-size:22px; color:#ffffff; letter-spacing:0.3px;">${safeSubject}</h1>
              <div style="margin:12px 0 0 0;">${safeContent}</div>
            </td>
          </tr>
          <tr>
            <td class="p-22" style="background-color:#000000; padding:22px; text-align:center; border-top:1px solid #1a1a1a;">
              <div style="display:inline-block; line-height:1;">
                <div class="logo" style="font-size:20px; font-weight:900; letter-spacing:-0.3px; color:#ffffff;">CRA</div>
                <div class="logo" style="font-size:20px; font-weight:900; letter-spacing:-0.3px; color:#ffffff;">PULE</div>
              </div>
              <div style="margin-top:10px; font-size:12px; color:#9ca3af;">Un soir où la cuisine rencontre le son.</div>
              <div style="margin-top:10px; font-size:12px; color:#9ca3af;">
                <a href="https://crapule.xyz" style="color:#e5e7eb; text-decoration:none;">crapule.xyz</a>
                <span style="color:#4b5563;"> • </span>
                <a href="https://instagram.com/crapule_bxl" style="color:#e5e7eb; text-decoration:none;">@crapule_bxl</a>
              </div>
              <div style="margin-top:10px; font-size:12px; color:#6b7280;">40, Quai des Charbonnages, 1080 Molenbeek, Bruxelles</div>
              <div style="margin-top:10px; font-size:12px; color:#6b7280;">© 2026 Crapule. Tous droits réservés.</div>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
  };
  const computeGroupEmails = (group) => {
    try {
      const list = (clients?.clients || [])
        .map(c => ({ email: c?.email, source: c?.source }))
        .filter(c => !!c.email);
      const uniq = (arr) => Array.from(new Set(arr));
      if (group === 'reservations') return uniq(list.filter(c => c.source === 'reservation').map(c => c.email)).join(', ');
      if (group === 'newsletter') return uniq(list.filter(c => c.source === 'newsletter').map(c => c.email)).join(', ');
      if (group === 'all') {
        const all = clients?.emailsFormatted || uniq(list.map(c => c.email)).join(', ');
        return all;
      }
      return '';
    } catch {
      return '';
    }
  };
  useEffect(() => {
    if (composeGroup === 'custom') {
      return;
    }
    const emails = computeGroupEmails(composeGroup);
    setComposeTo(emails);
  }, [composeGroup]);
  const reloadEmails = async () => {
    setRefreshing(true);
    try {
      const [rc, allRc, mt, se, ob] = await Promise.all([
        adminAPI.get('/notifications/received', { params: { q: receivedQuery, sort: receivedSort, prefix: receivedPrefix, deleted: receivedFolder === 'deleted', details: false } }),
        adminAPI.get('/notifications/received', { params: { q: '', sort: receivedSort, deleted: false, details: false } }),
        adminAPI.get('/notifications/received/metrics'),
        adminAPI.get('/notifications/sent', { params: { q: sentQuery, sort: sentSort } }),
        adminAPI.get('/notifications/outbox'),
      ]);
      const unread = (rc.data.items || []).filter(it => !it.read).length;
      const unreadAll = mt.data.unreadTotal || 0;
      setReceived({ items: rc.data.items, total: rc.data.total, resendConfigured: rc.data.resendConfigured, unread });
      setAllReceived({ items: allRc.data.items, total: allRc.data.total });
      setMetrics(mt.data);
      setSent(se.data);
      setOutbox(ob.data);
      setEmailsUnread(unreadAll);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      const res = await adminAPI.get('/notifications/config');
      setCfg(res.data);
      try {
        const fromCfg = toSerializableTemplates(res.data?.contentTemplates || []);
        if (fromCfg.length > 0) {
          setTemplatesState(fromCfg);
        }
      } catch {}
      const st = await adminAPI.get('/notifications/status');
      setSmtpStatus(st.data);
      const ob = await adminAPI.get('/notifications/outbox');
      setOutbox(ob.data);
      const se = await adminAPI.get('/notifications/sent', { params: { q: sentQuery, sort: sentSort } });
      setSent(se.data);
      const [rc, allRc, mt] = await Promise.all([
        adminAPI.get('/notifications/received', { params: { q: receivedQuery, sort: receivedSort, prefix: receivedPrefix, deleted: receivedFolder === 'deleted', details: false } }),
        adminAPI.get('/notifications/received', { params: { q: '', sort: receivedSort, deleted: false, details: false } }),
        adminAPI.get('/notifications/received/metrics'),
      ]);
      const unread = (rc.data.items || []).filter(it => !it.read).length;
      const unreadAll = mt.data.unreadTotal || 0;
      setReceived({ items: rc.data.items, total: rc.data.total, resendConfigured: rc.data.resendConfigured, unread });
      setAllReceived({ items: allRc.data.items, total: allRc.data.total });
      setMetrics(mt.data);
      setEmailsUnread(unreadAll);
    };
    load();
  }, []);

  useEffect(() => {
    const fetchSent = async () => {
      const se = await adminAPI.get('/notifications/sent', { params: { q: sentQuery, sort: sentSort } });
      setSent(se.data);
    };
    fetchSent();
  }, [sentQuery, sentSort]);

  useEffect(() => {
    const fetchReceived = async () => {
      const [rc, allRc, mt] = await Promise.all([
        adminAPI.get('/notifications/received', { params: { q: receivedQuery, sort: receivedSort, prefix: receivedPrefix, deleted: receivedFolder === 'deleted', details: false } }),
        adminAPI.get('/notifications/received', { params: { q: '', sort: receivedSort, deleted: false, details: false } }),
        adminAPI.get('/notifications/received/metrics'),
      ]);
      const unread = (rc.data.items || []).filter(it => !it.read).length;
      const unreadAll = mt.data.unreadTotal || 0;
      setReceived({ items: rc.data.items, total: rc.data.total, resendConfigured: rc.data.resendConfigured, unread });
      setAllReceived({ items: allRc.data.items, total: allRc.data.total });
      setMetrics(mt.data);
      setEmailsUnread(unreadAll);
    };
    fetchReceived();
  }, [receivedQuery, receivedSort, receivedPrefix, receivedFolder]);
  const save = async () => {
    setSaving(true);
    try {
      await adminAPI.put('/notifications/config', cfg);
    } finally {
      setSaving(false);
    }
  };

  const updateAndSave = async (nextCfg) => {
    setCfg(nextCfg);
    setSaving(true);
    try {
      await adminAPI.put('/notifications/config', nextCfg);
    } finally {
      setSaving(false);
    }
  };

  const testSend = async () => {
    setTesting(true);
    try {
      const res = await adminAPI.post('/notifications/test');
      toast({ title: 'Email de test', description: 'Envoi planifié', });
    } finally {
      setTesting(false);
    }
  };

  if (!cfg) {
    return (
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-8 text-center">
          <div className="mx-auto mb-4 w-8 h-8 rounded-full border-b-2 border-blue-400 animate-spin"></div>
          <p className="text-slate-400">Chargement de la configuration...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <style>
        {`.email-content a { text-decoration: none !important; }
           .email-content a:hover { text-decoration: none !important; }`}
      </style>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestion des Emails</h2>
          <p className="text-slate-400">
            {(metrics?.unreadTotal || 0)} email(s) non lu(s){(outbox?.total || 0) > 0 ? ` • ${outbox.total} en attente` : ''}
          </p>
        </div>
      </div>
      <div className="grid gap-4 items-start md:grid-cols-4">
            <div className="p-3 rounded-lg border bg-slate-900 border-slate-700 self-start md:max-h-[calc(100vh-12rem)] md:overflow-y-auto">
              <div className="flex justify-between items-center">
                <div className="text-xs tracking-wide uppercase text-slate-400">Mails</div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={reloadEmails}
                  className="p-2 text-slate-300 hover:text-white hover:bg-slate-800"
                  title="Actualiser"
                >
                  <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
                </Button>
              </div>
              <div className="mt-2 space-y-2">
                <Button
                  variant="ghost"
                  className={`justify-start w-full ${composeOpen ? 'text-white border bg-slate-800/40 border-slate-700' : 'text-slate-300 hover:bg-slate-700/30'}`}
                  onClick={() => setComposeOpen(true)}
                >
                  <MailPlus size={16} />
                  Écrire
                </Button>
                <Button
                  variant="ghost"
                  className={`w-full justify-between ${(receivedOpen || mailTab === 'received') ? 'text-white border bg-slate-800/60 border-slate-700' : 'text-slate-300 hover:bg-slate-700/30 hover:text-white'}`}
                  onClick={() => setReceivedOpen(o => !o)}
                >
                  <span className="inline-flex items-center">
                    <Mail size={16} className="mr-2" />
                    Reçus
                  </span>
                  {receivedOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </Button>
              </div>
              {receivedOpen && (
              <div className="pl-4 mt-2 space-y-2">
                <Button
                  variant={mailTab === 'received' && receivedFolder === 'inbox' && !receivedPrefix ? 'default' : 'ghost'}
                  className={`w-full justify-start ${mailTab === 'received' && receivedFolder === 'inbox' && !receivedPrefix ? 'text-black' : 'text-slate-300 hover:bg-slate-700/30 hover:text-white'}`}
                  onClick={() => { setMailTab('received'); setReceivedFolder('inbox'); setReceivedPrefix(''); setReceivedPage(1); }}
                >
                  <span>Tous</span>
                  <span className="inline-flex gap-1 items-center ml-auto">
                    {allStats.unread > 0 && (
                      <Badge className="px-1 py-0.5 bg-red-600/20 text-red-300 border-red-600/40">{allStats.unread}</Badge>
                    )}
                  </span>
                </Button>
                {prefixOptions.map((p) => (
                  <Button
                    key={p}
                    variant={mailTab === 'received' && receivedFolder === 'inbox' && receivedPrefix === p ? 'default' : 'ghost'}
                    className={`w-full justify-start ${mailTab === 'received' && receivedFolder === 'inbox' && receivedPrefix === p ? 'text-black' : 'text-slate-300 hover:bg-slate-700/30 hover:text-white'}`}
                    onClick={() => { setMailTab('received'); setReceivedFolder('inbox'); setReceivedPrefix(p); setReceivedPage(1); }}
                  >
                    <span>{p}@{senderDomain}</span>
                    <span className="inline-flex gap-1 items-center ml-auto">
                      {(prefixStats[p]?.unread || 0) > 0 && (
                        <Badge className="px-1 py-0.5 bg-red-600/20 text-red-300 border-red-600/40">{prefixStats[p].unread}</Badge>
                      )}
                    </span>
                  </Button>
                ))}
                <Button
                  variant={mailTab === 'received' && receivedFolder === 'deleted' ? 'default' : 'ghost'}
                  className={`w-full justify-start ${mailTab === 'received' && receivedFolder === 'deleted' ? 'text-black' : 'text-slate-300 hover:bg-slate-700/30 hover:text-white'}`}
                  onClick={() => { setMailTab('received'); setReceivedFolder('deleted'); setReceivedPrefix(''); setReceivedPage(1); }}
                >
                  <span>Supprimés</span>
                </Button>
              </div>
              )}
              <div className="mt-4 space-y-2">
                  <Button
                    variant={mailTab === 'sent' ? 'default' : 'ghost'}
                    className={`w-full justify-start ${mailTab === 'sent' ? 'text-black' : 'text-slate-300 hover:bg-slate-700/30 hover:text-white'}`}
                    onClick={() => setMailTab('sent')}
                  >
                    <span className="inline-flex items-center">
                      <Send size={16} className="mr-2" />
                      Envoyés
                    </span>
                  </Button>
                  <Button
                    variant={mailTab === 'outbox' ? 'default' : 'ghost'}
                    className={`w-full justify-between ${mailTab === 'outbox' ? 'text-black' : 'text-slate-300 hover:bg-slate-700/30 hover:text-white'}`}
                    onClick={() => setMailTab('outbox')}
                  >
                  <span className="inline-flex items-center">
                    <Clock size={16} className="mr-2" />
                    File d’attente
                  </span>
                  {outbox.total > 0 && (
                    <Badge className="px-1 py-0.5 bg-yellow-600/20 text-yellow-300 border-yellow-600/40">{outbox.total}</Badge>
                  )}
                  </Button>
                  <Button
                    variant={mailTab === 'center' ? 'default' : 'ghost'}
                    className={`w-full justify-start ${mailTab === 'center' ? 'text-black' : 'text-slate-300 hover:bg-slate-700/30 hover:text-white'}`}
                    onClick={() => setMailTab('center')}
                  >
                    <span className="inline-flex items-center">
                      <Settings size={16} className="mr-2" />
                      Paramètres
                    </span>
                  </Button>
              </div>
            </div>
            <div className="md:col-span-3">
              <Tabs value={mailTab} onValueChange={setMailTab} className="w-full">
            <TabsContent value="outbox">
              {outbox.total === 0 ? (
                <p className="text-slate-400">Aucun message en attente</p>
              ) : (
                <>
                  <div className="flex gap-3 items-start p-3 mb-3 rounded-lg border bg-yellow-500/10 border-yellow-500/30">
                    <svg xmlns="http://www.w3.org/2000/svg" className="mt-0.5 w-5 h-5 text-yellow-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 9v4" />
                      <path d="M12 17h.01" />
                      <path d="M21.73 18a2 2 0 0 1-1.73 1H4a2 2 0 0 1-1.73-1L.27 9a2 2 0 0 1 1.73-3h20a2 2 0 0 1 1.73 3Z" />
                    </svg>
                    <div className="text-sm text-yellow-200">
                      <span className="font-semibold">Adresse email invalide ou syntaxe incorrecte.</span>
                      <br />
                      Vérifiez et corrigez le(s) destinataire(s) dans le champ ci-dessous avant de cliquer sur <span className="font-semibold">Envoyer à nouveau</span>.
                    </div>
                  </div>
                  <Accordion type="single" collapsible className="w-full rounded-md border bg-slate-900/40 border-slate-700">
                    {outbox.items.map((m, idx) => (
                      <AccordionItem key={m.id || idx} value={`${m.id || idx}-o`} className="">
                        <AccordionTrigger className="px-3 py-2 text-white no-underline border-b transition-colors hover:bg-slate-800 hover:no-underline border-slate-700">
                          <div className="flex justify-between items-center w-full">
                            <div className="text-left">
                              <div className="font-medium">{m.subject || '(Sans sujet)'}</div>
                              <div className="text-sm text-slate-300">Prévu le {formatDateTime(m.createdAt)}</div>
                            </div>
                            <div className="flex flex-wrap gap-2 items-center">
                              {(Array.isArray(m.recipients) && m.recipients.length > 0) && (
                                <Badge className="text-emerald-300 bg-emerald-600/20 border-emerald-600/40">À: {m.recipients[0]}</Badge>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  await adminAPI.delete(`/notifications/outbox/${m.id}`);
                                  const ob = await adminAPI.get('/notifications/outbox');
                                  setOutbox(ob.data);
                                  setEmailsQueued(ob.data?.total || (ob.data?.items?.length || 0) || 0);
                                }}
                                className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                                title="Supprimer"
                              >
                                <Trash2 size={16} />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={async (e) => { e.stopPropagation(); await resendQueuedEmail(m); }}
                                className="text-emerald-300 hover:text-emerald-400 hover:bg-emerald-500/10"
                                title="Envoyer"
                              >
                                <Send size={16} />
                              </Button>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="p-3 space-y-2 bg-slate-900/60">
                            <div className="space-y-2">
                              <div className="text-sm text-slate-300">Modifier destinataires</div>
                              <Input
                                value={outboxEdits[m.id] ?? getRecipientsString(m)}
                                onChange={(e) => setOutboxEdits(prev => ({ ...prev, [m.id]: e.target.value }))}
                                placeholder="email1@example.com, email2@example.com"
                                className="text-white bg-slate-900 border-slate-700"
                              />
                              <div className="flex gap-2 justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => { await resendQueuedEmail(m); }}
                                  className="border-slate-700 text-slate-300 hover:text-white hover:bg-slate-700"
                                >
                                  Envoyer maintenant
                                </Button>
                              </div>
                            </div>
                            <div className="mt-2 text-sm text-slate-300 email-content" dangerouslySetInnerHTML={{ __html: m.body }} />
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  <div className="flex justify-end mt-3">
                    <Button
                      onClick={async () => {
                        await adminAPI.post('/notifications/outbox/flush');
                        const ob = await adminAPI.get('/notifications/outbox');
                        setOutbox(ob.data);
                        setEmailsQueued(ob.data?.total || (ob.data?.items?.length || 0) || 0);
                        const se = await adminAPI.get('/notifications/sent', { params: { q: sentQuery, sort: sentSort } });
                        setSent(se.data);
                      }}
                      className="text-black bg-white hover:bg-gray-200"
                      disabled={!smtpStatus.smtpConfigured}
                    >
                      Envoyer en attente
                    </Button>
                  </div>
                </>
              )}
            </TabsContent>
            <TabsContent value="sent">
              <div className="flex flex-col gap-2 mb-3 sm:flex-row sm:items-center sm:justify-between">
                <Input
                  value={sentQuery}
                  onChange={(e) => setSentQuery(e.target.value)}
                  placeholder="Rechercher par sujet, contenu ou destinataire"
                  className="w-full text-white bg-slate-900 border-slate-700"
                />
                <div className="flex gap-2">
                  <Select value={sentSort} onValueChange={setSentSort}>
                    <SelectTrigger className="w-full h-10 text-white bg-slate-900 border-slate-700">
                      <SelectValue placeholder="Trier par" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="-createdAt" className="text-white">Date (plus récent)</SelectItem>
                      <SelectItem value="createdAt" className="text-white">Date (plus ancien)</SelectItem>
                      <SelectItem value="subject" className="text-white">Sujet (A→Z)</SelectItem>
                      <SelectItem value="-subject" className="text-white">Sujet (Z→A)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={String(sentPageSize)} onValueChange={(v) => { setSentPageSize(parseInt(v, 10)); setSentPage(1); }}>
                    <SelectTrigger className="w-full h-10 text-white bg-slate-900 border-slate-700">
                      <SelectValue placeholder="Par page" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5" className="text-white">5 par page</SelectItem>
                      <SelectItem value="10" className="text-white">10 par page</SelectItem>
                      <SelectItem value="20" className="text-white">20 par page</SelectItem>
                      <SelectItem value="50" className="text-white">50 par page</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {sent.total === 0 ? (
                <p className="text-slate-400">Aucun email envoyé</p>
              ) : (
                <>
                  <Accordion type="single" collapsible className="w-full rounded-md border bg-slate-900/40 border-slate-700">
                    {sent.items.slice((sentPage - 1) * sentPageSize, sentPage * sentPageSize).map((m, idx) => (
                      <AccordionItem key={`${m.id || idx}-${idx}`} value={`${m.id || idx}`} className="">
                        <AccordionTrigger className="px-3 py-2 text-white no-underline border-b transition-colors hover:bg-slate-800 hover:no-underline border-slate-700">
                          <div className="grid grid-cols-1 items-center w-full gap-2 md:grid-cols-[1fr,120px,80px,auto]">
                            <div className="font-medium truncate">{m.subject || '(Sans sujet)'}</div>
                            <div className="text-sm text-slate-300">
                              {(() => { try { return new Date(m.createdAt).toLocaleDateString('fr-BE'); } catch { return '—'; } })()}
                            </div>
                            <div className="text-sm text-slate-300">
                              {(() => { try { return new Date(m.createdAt).toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' }); } catch { return '—'; } })()}
                            </div>
                            <div className="flex flex-wrap gap-2 justify-start items-center md:justify-end">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={async (e) => { e.stopPropagation(); await resendSentEmail(m); }}
                                className="text-emerald-300 hover:text-emerald-400 hover:bg-emerald-500/10"
                                title="Envoyer à nouveau"
                              >
                                <Send size={16} />
                              </Button>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="p-3 space-y-2 bg-slate-900/60">
                            <div className="text-sm text-slate-300">De: {m.from}</div>
                            <div className="space-y-2">
                              <div className="text-sm text-slate-300">Modifier destinataires</div>
                              <Input
                                value={sentEdits[m.id] ?? getRecipientsString(m)}
                                onChange={(e) => setSentEdits(prev => ({ ...prev, [m.id]: e.target.value }))}
                                placeholder="email1@example.com, email2@example.com"
                                className="text-white bg-slate-900 border-slate-700"
                              />
                              <div className="flex gap-2 justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => { await resendSentEmail(m); }}
                                  className="border-slate-700 text-slate-300 hover:text-white hover:bg-slate-700"
                                >
                                  Envoyer à nouveau
                                </Button>
                              </div>
                            </div>
                            <div className="mt-2 text-sm text-slate-300 email-content" dangerouslySetInnerHTML={{ __html: m.body }} />
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  <div className="flex justify-between items-center mt-3">
                    <div className="text-sm text-slate-400">
                      Page {sentPage} / {Math.max(1, Math.ceil((sent.total || 0) / sentPageSize))}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" disabled={sentPage <= 1} onClick={() => setSentPage(p => Math.max(1, p - 1))} className="border-slate-700">Précédent</Button>
                      <Button variant="outline" disabled={sentPage >= Math.ceil((sent.total || 0) / sentPageSize)} onClick={() => setSentPage(p => p + 1)} className="border-slate-700">Suivant</Button>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>
            <TabsContent value="received">
              {!received.resendConfigured ? (
                <div className="p-3 text-sm text-yellow-300 rounded border bg-yellow-500/10 border-yellow-500/20">
                  Réception via Resend non configurée.
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-2 mb-3 sm:flex-row sm:items-center sm:justify-between">
                    <Input
                      value={receivedQuery}
                      onChange={(e) => setReceivedQuery(e.target.value)}
                      placeholder="Rechercher par sujet, expéditeur, destinataire"
                      className="w-full text-white bg-slate-900 border-slate-700"
                    />
                    <div className="flex gap-2">
                      <Select value={receivedSort} onValueChange={setReceivedSort}>
                        <SelectTrigger className="w-full h-10 text-white bg-slate-900 border-slate-700">
                          <SelectValue placeholder="Trier par" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="-created_at" className="text-white">Date (plus récent)</SelectItem>
                          <SelectItem value="created_at" className="text-white">Date (plus ancien)</SelectItem>
                          <SelectItem value="subject" className="text-white">Sujet (A→Z)</SelectItem>
                          <SelectItem value="-subject" className="text-white">Sujet (Z→A)</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={String(receivedPageSize)} onValueChange={(v) => { setReceivedPageSize(parseInt(v, 10)); setReceivedPage(1); }}>
                        <SelectTrigger className="w-full h-10 text-white bg-slate-900 border-slate-700">
                          <SelectValue placeholder="Par page" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5" className="text-white">5 par page</SelectItem>
                          <SelectItem value="10" className="text-white">10 par page</SelectItem>
                          <SelectItem value="20" className="text-white">20 par page</SelectItem>
                          <SelectItem value="50" className="text-white">50 par page</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                      {received.total === 0 ? (
                        <p className="text-slate-400">Aucun email reçu</p>
                      ) : (
                        <>
                          <Accordion type="single" collapsible className="w-full rounded-md border bg-slate-900/40 border-slate-700">
                            {received.items.slice((receivedPage - 1) * receivedPageSize, receivedPage * receivedPageSize).map((m, idx) => (
                                <AccordionItem key={`${m.id || idx}-r-${idx}`} value={`${m.id || idx}-r`} className="">
                            <AccordionTrigger
                              className="px-3 py-2 text-white no-underline border-b transition-colors hover:bg-slate-800 hover:no-underline border-slate-700"
                                  onClick={async () => {
                                    const eid = m.id || m.email_id;
                                    if (!eid) return;
                                    try {
                                      if (!m.read) {
                                        await adminAPI.put(`/notifications/received/${eid}/read`);
                                        setReceived(prev => {
                                          const items = prev.items.map((x) => {
                                            if ((x.id || x.email_id) === eid) return { ...x, read: true };
                                            return x;
                                          });
                                          const unread = items.filter(it => !it.read).length;
                                          setEmailsUnread(unread);
                                          return { ...prev, items, unread };
                                        });
                                      }
                                      const det = await adminAPI.get(`/notifications/received/${eid}`);
                                      setReceived(prev => {
                                        const items = prev.items.map(x => ((x.id || x.email_id) === eid) ? { ...x, ...det.data } : x);
                                        return { ...prev, items };
                                      });
                                      try {
                                        const mt = await adminAPI.get('/notifications/received/metrics');
                                        setMetrics(mt.data);
                                        setEmailsUnread(mt.data.unreadTotal || 0);
                                      } catch {}
                                    } catch (e) {
                                      // ignore
                                    }
                                  }}
                                  >
                                  <div className="grid grid-cols-1 items-center w-full gap-2 md:grid-cols-[1fr,120px,80px,auto]">
                                    <div className="font-medium truncate">{m.subject || '(Sans sujet)'}</div>
                                    <div className="text-sm text-slate-300">
                                      {(() => { try { return new Date(m.created_at).toLocaleDateString('fr-BE'); } catch { return '—'; } })()}
                                    </div>
                                    <div className="text-sm text-slate-300">
                                      {(() => { try { return new Date(m.created_at).toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' }); } catch { return '—'; } })()}
                                    </div>
                                    <div className="flex flex-wrap gap-2 justify-start items-center md:justify-end">
                                      {!m.read && (
                                        <Badge className="text-white bg-red-600/20 border-red-600/40">Nouveau</Badge>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-blue-300 hover:text-blue-400 hover:bg-blue-500/10"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const to = extractEmail(m.from);
                                          setComposeTo(to || '');
                                          setComposeSubject(`Re: ${m.subject || ''}`);
                                          setComposeReplyTo('');
                                          setComposeCc('');
                                          setComposeBcc('');
                                          setComposeHtml('');
                                          setComposeUseTemplate(true);
                                          setReplyContext({
                                            inReplyTo: m.message_id || m['message-id'] || m.id || m.email_id,
                                            references: (m.references && Array.isArray(m.references)) ? m.references : [],
                                          });
                                          setReplyOpen(true);
                                        }}
                                      >
                                        <Reply size={16} />
                                      </Button>
                                      {!m.deleted && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="text-red-300 hover:text-red-400 hover:bg-red-500/10"
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            const eid = m.id || m.email_id;
                                            if (!eid) return;
                                            try {
                                              await adminAPI.put(`/notifications/received/${eid}/delete`);
                                              await reloadEmails();
                                            } catch {}
                                          }}
                                        >
                                          <Trash2 size={16} />
                                        </Button>
                                      )}
                                      {m.deleted && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="text-emerald-300 hover:text-emerald-400 hover:bg-emerald-500/10"
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            const eid = m.id || m.email_id;
                                            if (!eid) return;
                                            try {
                                              await adminAPI.put(`/notifications/received/${eid}/undelete`);
                                              await reloadEmails();
                                            } catch {}
                                          }}
                                        >
                                          <RotateCcw size={16} />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent>
                                  <div className="p-3 space-y-2 bg-slate-900/60">
                                    <div className="text-sm text-slate-300">De: {m.from}</div>
                                    <div className="text-sm text-slate-300">À: {(m.to || []).join(', ')}</div>
                                    {m.html ? (
                                      <div className="mt-2 text-sm text-slate-300 email-content" dangerouslySetInnerHTML={{ __html: m.html }} />
                                    ) : m.text ? (
                                      <div className="mt-2 text-sm text-slate-300 email-content">{m.text}</div>
                                    ) : null}
                                <div className="flex justify-end mt-2">
                                  {m.read && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        const eid = m.id || m.email_id;
                                        if (!eid) return;
                                        try {
                                          await adminAPI.put(`/notifications/received/${eid}/unread`);
                                          setReceived(prev => {
                                            const items = prev.items.map(x => ((x.id || x.email_id) === eid) ? { ...x, read: false, readAt: null } : x);
                                            const unread = items.filter(it => !it.read).length;
                                            return { ...prev, items, unread };
                                          });
                                          setAllReceived(prev => {
                                            const items = prev.items.map(x => ((x.id || x.email_id) === eid) ? { ...x, read: false, readAt: null } : x);
                                            const unreadAll = items.filter(it => !it.read).length;
                                            setEmailsUnread(unreadAll);
                                            return { ...prev, items };
                                          });
                                        } catch (err) {
                                          // ignore
                                        }
                                      }}
                                    >
                                      Marquer non lu
                                    </Button>
                                  )}
                                </div>
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                          <div className="flex justify-between items-center mt-3">
                            <div className="text-sm text-slate-400">
                              Page {receivedPage} / {Math.max(1, Math.ceil((received.total || 0) / receivedPageSize))}
                            </div>
                            <div className="flex gap-2">
                              <Button variant="outline" disabled={receivedPage <= 1} onClick={() => setReceivedPage(p => Math.max(1, p - 1))} className="border-slate-700">Précédent</Button>
                              <Button variant="outline" disabled={receivedPage >= Math.ceil((received.total || 0) / receivedPageSize)} onClick={() => setReceivedPage(p => p + 1)} className="border-slate-700">Suivant</Button>
                            </div>
                          </div>
                        </>
                      )}
                  </div>
                </>
              )}
            </TabsContent>
            {/* Compose moved to modal */}
            {replyOpen && (
              <Dialog open={replyOpen} onOpenChange={(o) => { setReplyOpen(o); if (!o) resetReplyFields(); }}>
                <DialogContent className="max-w-3xl text-white bg-slate-950 border-slate-800">
                  <DialogHeader>
                    <DialogTitle>Répondre</DialogTitle>
                    <DialogDescription>Composer une réponse à l’email reçu.</DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label className="text-slate-300">À</Label>
                          <Input
                            value={composeTo}
                            onChange={(e) => setComposeTo(e.target.value)}
                            placeholder="destinataire"
                            className="text-white bg-slate-900 border-slate-700"
                          />
                          <Label className="text-slate-300">Objet</Label>
                          <Input
                            value={composeSubject}
                            onChange={(e) => setComposeSubject(e.target.value)}
                            placeholder="Re: ..."
                            className="text-white bg-slate-900 border-slate-700"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-slate-300">Reply-To (optionnel)</Label>
                          <Input
                            value={composeReplyTo}
                            onChange={(e) => setComposeReplyTo(e.target.value)}
                            placeholder="reply@example.com"
                            className="text-white bg-slate-900 border-slate-700"
                          />
                          <div className="flex gap-2">
                            <div className="flex-1">
                              <Label className="text-slate-300">Cc</Label>
                              <Input
                                value={composeCc}
                                onChange={(e) => setComposeCc(e.target.value)}
                                placeholder="cc@exemple.com"
                                className="text-white bg-slate-900 border-slate-700"
                              />
                            </div>
                            <div className="flex-1">
                              <Label className="text-slate-300">Bcc</Label>
                              <Input
                                value={composeBcc}
                                onChange={(e) => setComposeBcc(e.target.value)}
                                placeholder="bcc@exemple.com"
                                className="text-white bg-slate-900 border-slate-700"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Modèle de contenu</Label>
                        <Select
                          value={replyTemplateId}
                          onValueChange={(v) => {
                            const id = v === 'none' ? '' : v;
                            setReplyTemplateId(id);
                            const html = id ? applyLocalTemplate(id) : '';
                            setComposeHtml(html);
                          }}
                        >
                      <SelectTrigger className="w-full text-white bg-slate-900 border-slate-700">
                        <SelectValue placeholder="Sélectionner un modèle" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                            <SelectItem value="none" className="text-white">Aucun</SelectItem>
                            {templatesState.map(t => (
                          <SelectItem key={t.id} value={t.id} className="text-white">{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                      <div className="space-y-2">
                        <div className="flex gap-3 items-center">
                          <Label className="text-slate-300">Format</Label>
                          <Switch
                            checked={replyFormat === 'html'}
                            onCheckedChange={(checked) => setReplyFormat(checked ? 'html' : 'text')}
                            className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-slate-600"
                          />
                          <span className="text-sm text-slate-300">{replyFormat === 'html' ? 'HTML' : 'Texte brut'}</span>
                        </div>
                        <Label className="text-slate-300">Contenu ({replyFormat === 'html' ? 'HTML' : 'texte'})</Label>
                        <textarea
                          value={composeHtml}
                          onChange={(e) => setComposeHtml(e.target.value)}
                          rows={10}
                          className="p-3 w-full text-white rounded border bg-slate-900 border-slate-700"
                        />
                        <div className="flex gap-2 items-center">
                          <Switch
                            checked={composeUseTemplate}
                            onCheckedChange={setComposeUseTemplate}
                            className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-slate-600"
                          />
                          <span className="text-sm text-slate-300">Appliquer le template Crapule</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 mt-2 sm:flex-row sm:justify-end">
                        <Button
                          onClick={async () => {
                            if (!composeSubject || !composeTo) {
                              toast({ title: 'Champs requis', description: 'Sujet et destinataires sont obligatoires' });
                              return;
                            }
                            setComposeSending(true);
                            try {
                              const to = composeTo.split(',').map(s => s.trim()).filter(Boolean);
                              const cc = composeCc ? composeCc.split(',').map(s => s.trim()).filter(Boolean) : [];
                              const bcc = composeBcc ? composeBcc.split(',').map(s => s.trim()).filter(Boolean) : [];
                              const replyTo = composeReplyTo ? [composeReplyTo.trim()] : [];
                              const contentForHtml = replyFormat === 'html'
                                ? composeHtml
                                : `<pre style="white-space:pre-wrap">${escapeHtml(composeHtml || '')}</pre>`;
                              const finalHtml = composeUseTemplate ? buildEmailHtml(contentForHtml, composeSubject) : contentForHtml;
                              const refs = (replyContext?.references || []).map(String).filter(Boolean);
                              const payload = {
                                prefix: null,
                                displayName: null,
                                to,
                                subject: composeSubject,
                                html: finalHtml,
                                cc,
                                bcc,
                                replyTo,
                                inReplyTo: replyContext?.inReplyTo || null,
                                references: replyContext?.inReplyTo ? [...refs, replyContext.inReplyTo] : refs,
                              };
                              await adminAPI.post('/notifications/compose/send', payload);
                              const se = await adminAPI.get('/notifications/sent', { params: { q: sentQuery, sort: sentSort } });
                              setSent(se.data);
                              toast({ title: 'Réponse envoyée', description: 'Votre email a été envoyé via Resend' });
                              setReplyOpen(false);
                            } finally {
                              setComposeSending(false);
                            }
                          }}
                          disabled={composeSending}
                          className="text-black bg-white hover:bg-gray-200"
                        >
                          Envoyer la réponse
                        </Button>
                      </div>
                    </div>
                    <div className="rounded border border-slate-800 h-[60vh] overflow-hidden">
                      <iframe
                        title="reply-preview"
                        style={{ width: '100%', height: '100%', border: '0', background: 'white' }}
                        srcDoc={sanitizeForPreview(
                          composeUseTemplate
                            ? buildEmailHtml(
                                replyFormat === 'html'
                                  ? composeHtml
                                  : `<pre style="white-space:pre-wrap">${escapeHtml(composeHtml || '')}</pre>`,
                                composeSubject
                              )
                            : (
                                replyFormat === 'html'
                                  ? (composeHtml || '<p>(Sans contenu)</p>')
                                  : `<pre style="white-space:pre-wrap">${escapeHtml(composeHtml || '')}</pre>`
                              )
                        )}
                        sandbox="allow-same-origin"
                      />
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            {composeOpen && (
              <Dialog open={composeOpen} onOpenChange={(o) => { setComposeOpen(o); if (!o) resetComposeFields(); }}>
                <DialogContent className="max-w-5xl text-white bg-slate-950 border-slate-800">
                  <DialogHeader>
                    <DialogTitle>Écrire un email</DialogTitle>
                    <DialogDescription>Composer et prévisualiser votre message.</DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label className="text-slate-300">Expéditeur (préfixe)</Label>
                          <div className="flex gap-2">
                            <Input
                              value={composePrefix}
                              onChange={(e) => setComposePrefix(e.target.value)}
                              placeholder="prefixe"
                              className="text-white bg-slate-900 border-slate-700"
                            />
                            <div className="px-3 py-2 text-white rounded border bg-slate-900 border-slate-700">@crapule.xyz</div>
                          </div>
                          <Label className="text-slate-300">Nom d’affichage</Label>
                          <Input
                            value={composeDisplayName}
                            onChange={(e) => setComposeDisplayName(e.target.value)}
                            placeholder="Crapule"
                            className="text-white bg-slate-900 border-slate-700"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-slate-300">Destinataires (groupe)</Label>
                          <Select value={composeGroup} onValueChange={setComposeGroup}>
                            <SelectTrigger className="w-full text-white bg-slate-900 border-slate-700">
                              <SelectValue placeholder="Sélectionner un groupe" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-700">
                              <SelectItem value="custom" className="text-white">Personnalisé</SelectItem>
                              <SelectItem value="reservations" className="text-white">Réservations</SelectItem>
                              <SelectItem value="newsletter" className="text-white">Newsletter</SelectItem>
                              <SelectItem value="all" className="text-white">Tous</SelectItem>
                            </SelectContent>
                          </Select>
                          <Label className="text-slate-300">Destinataires</Label>
                          <Input
                            value={composeTo}
                            onChange={(e) => setComposeTo(e.target.value)}
                            placeholder="email1@example.com, email2@example.com"
                            className="text-white bg-slate-900 border-slate-700"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label className="text-slate-300">Cc</Label>
                          <Input
                            value={composeCc}
                            onChange={(e) => setComposeCc(e.target.value)}
                            placeholder="cc@example.com"
                            className="text-white bg-slate-900 border-slate-700"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-slate-300">Bcc</Label>
                          <Input
                            value={composeBcc}
                            onChange={(e) => setComposeBcc(e.target.value)}
                            placeholder="bcc@example.com"
                            className="text-white bg-slate-900 border-slate-700"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Reply-To</Label>
                        <Input
                          value={composeReplyTo}
                          onChange={(e) => setComposeReplyTo(e.target.value)}
                          placeholder="reply@example.com"
                          className="text-white bg-slate-900 border-slate-700"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Objet</Label>
                        <Input
                          value={composeSubject}
                          onChange={(e) => setComposeSubject(e.target.value)}
                          placeholder="Sujet de l’email"
                          className="text-white bg-slate-900 border-slate-700"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Modèle de contenu</Label>
                        <Select
                          value={composeTemplateId}
                          onValueChange={(v) => {
                            const id = v === 'none' ? '' : v;
                            setComposeTemplateId(id);
                            const html = id ? applyLocalTemplate(id) : '';
                            setComposeHtml(html);
                          }}
                        >
                          <SelectTrigger className="w-full text-white bg-slate-900 border-slate-700">
                            <SelectValue placeholder="Sélectionner un modèle" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-700">
                            <SelectItem value="none" className="text-white">Aucun</SelectItem>
                            {templatesState.map(t => (
                              <SelectItem key={t.id} value={t.id} className="text-white">{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <div className="flex gap-3 items-center">
                          <Label className="text-slate-300">Format</Label>
                          <Switch
                            checked={composeFormat === 'html'}
                            onCheckedChange={(checked) => setComposeFormat(checked ? 'html' : 'text')}
                            className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-slate-600"
                          />
                          <span className="text-sm text-slate-300">{composeFormat === 'html' ? 'HTML' : 'Texte brut'}</span>
                        </div>
                        <Label className="text-slate-300">Contenu ({composeFormat === 'html' ? 'HTML' : 'texte'})</Label>
                        <textarea
                          value={composeHtml}
                          onChange={(e) => setComposeHtml(e.target.value)}
                          rows={10}
                          className="p-3 w-full text-white rounded border bg-slate-900 border-slate-700"
                        />
                        <div className="flex gap-2 items-center">
                          <Switch
                            checked={composeUseTemplate}
                            onCheckedChange={setComposeUseTemplate}
                            className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-slate-600"
                          />
                          <span className="text-sm text-slate-300">Appliquer le template Crapule</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 mt-2 sm:flex-row sm:justify-end">
                        <Button
                          onClick={async () => {
                            if (!composeSubject || !composeTo) {
                              toast({ title: 'Champs requis', description: 'Sujet et destinataires sont obligatoires' });
                              return;
                            }
                            setComposeSending(true);
                            try {
                              const to = composeTo.split(',').map(s => s.trim()).filter(Boolean);
                              const cc = composeCc ? composeCc.split(',').map(s => s.trim()).filter(Boolean) : [];
                              const bcc = composeBcc ? composeBcc.split(',').map(s => s.trim()).filter(Boolean) : [];
                              const replyTo = composeReplyTo ? [composeReplyTo.trim()] : [];
                              const contentForHtml = composeFormat === 'html'
                                ? composeHtml
                                : `<pre style="white-space:pre-wrap">${escapeHtml(composeHtml || '')}</pre>`;
                              const finalHtml = composeUseTemplate ? buildEmailHtml(contentForHtml, composeSubject) : contentForHtml;
                              await adminAPI.post('/notifications/compose/send', {
                                prefix: composePrefix || null,
                                displayName: composeDisplayName || null,
                                to,
                                subject: composeSubject,
                                html: finalHtml,
                                cc,
                                bcc,
                                replyTo,
                              });
                              const se = await adminAPI.get('/notifications/sent', { params: { q: sentQuery, sort: sentSort } });
                              setSent(se.data);
                              toast({ title: 'Email envoyé', description: 'Votre email a été envoyé via Resend' });
                              setComposeOpen(false);
                            } finally {
                              setComposeSending(false);
                            }
                          }}
                          disabled={composeSending}
                          className="text-black bg-white hover:bg-gray-200"
                        >
                          Envoyer
                        </Button>
                      </div>
                    </div>
                    <div className="rounded border border-slate-800 h-[60vh] overflow-hidden">
                      <iframe
                        title="compose-preview"
                        style={{ width: '100%', height: '100%', border: '0', background: 'white' }}
                        srcDoc={sanitizeForPreview(
                          composeUseTemplate
                            ? buildEmailHtml(
                                composeFormat === 'html'
                                  ? composeHtml
                                  : `<pre style="white-space:pre-wrap">${escapeHtml(composeHtml || '')}</pre>`,
                                composeSubject
                              )
                            : (
                                composeFormat === 'html'
                                  ? (composeHtml || '<p>(Sans contenu)</p>')
                                  : `<pre style="white-space:pre-wrap">${escapeHtml(composeHtml || '')}</pre>`
                              )
                        )}
                        sandbox="allow-same-origin"
                      />
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            <TabsContent value="center">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="flex gap-2 items-center">
                    <Mail className="text-blue-400" />
                    <span className="font-medium text-white">Centre de Notifications</span>
                  </div>
                  <Badge className="flex gap-1 items-center bg-slate-700 text-slate-200 border-slate-600">
                    <Lock size={14} /> Ne pas modifier
                  </Badge>
                </div>
                <p className="text-slate-400">Réservé à la configuration technique. Ne pas toucher.</p>
                {!smtpStatus.smtpConfigured && (
                  <div className="p-3 text-sm text-yellow-300 rounded border bg-yellow-500/10 border-yellow-500/20">
                    SMTP non configuré. Les emails seront mis en file d’attente et non envoyés tant que la configuration n’est pas fournie.
                  </div>
                )}
                <div className="grid grid-cols-1 gap-4">
                  <Tabs value={settingsTab} onValueChange={setSettingsTab} className="w-full">
                  <TabsList className="flex flex-wrap gap-2 justify-start p-0 bg-transparent rounded-none">
                      <TabsTrigger value="general" className="px-3 py-1.5 rounded border border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800/40 data-[state=active]:bg-slate-800/60 data-[state=active]:text-white">Généraux</TabsTrigger>
                      <TabsTrigger value="recipients" className="px-3 py-1.5 rounded border border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800/40 data-[state=active]:bg-slate-800/60 data-[state=active]:text-white">Destinataires</TabsTrigger>
                      <TabsTrigger value="forward" className="px-3 py-1.5 rounded border border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800/40 data-[state=active]:bg-slate-800/60 data-[state=active]:text-white">Transfert reçus</TabsTrigger>
                      <TabsTrigger value="models" className="px-3 py-1.5 rounded border border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800/40 data-[state=active]:bg-slate-800/60 data-[state=active]:text-white">Modèles</TabsTrigger>
                      <TabsTrigger value="events" className="px-3 py-1.5 rounded border border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800/40 data-[state=active]:bg-slate-800/60 data-[state=active]:text-white">Gestion des événements</TabsTrigger>
                    </TabsList>
                    <TabsContent value="general">
                      <div className="p-4 rounded-lg border border-slate-700">
                        <div className="mb-3 font-medium text-white">Paramètres généraux</div>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <div className="flex gap-3 items-center">
                          <Switch
                            checked={cfg.enabled}
                            onCheckedChange={(v) => updateAndSave({ ...cfg, enabled: v })}
                            className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-slate-600"
                          />
                              <Label className="text-slate-300">Activer les mails</Label>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex gap-3 items-center">
                          <Switch
                            checked={cfg.sendToBooker}
                            onCheckedChange={(v) => updateAndSave({ ...cfg, sendToBooker: v })}
                            className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-slate-600"
                          />
                              <Label className="text-slate-300">Envoyer au client</Label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="recipients">
                      <div className="p-4 rounded-lg border border-slate-700">
                        <div className="mb-3 font-medium text-white">Destinataires additionnels</div>
                        <div className="flex flex-col gap-2 sm:flex-row">
                          <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="email@exemple.com" className="w-full text-white bg-slate-900 border-slate-700" />
                          <Button className="w-full sm:w-auto" onClick={async () => {
                            const e = newEmail.trim();
                            if (!e) return;
                            const nextCfg = { ...cfg, additionalEmails: [...(cfg.additionalEmails || []), e] };
                            await updateAndSave(nextCfg);
                            setNewEmail('');
                          }}>
                            <Plus size={16} className="mr-2" />
                            Ajouter
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {(cfg.additionalEmails || []).map((e, idx) => (
                            <Badge key={idx} className="px-2 py-1 bg-slate-700 text-slate-200 border-slate-600">
                              {e}
                              <button className="inline-flex items-center ml-2" onClick={async () => {
                                const nextCfg = { ...cfg, additionalEmails: (cfg.additionalEmails || []).filter(x => x !== e) };
                                await updateAndSave(nextCfg);
                              }}>
                                <Trash2 size={12} />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="forward">
                      <div className="p-4 rounded-lg border border-slate-700">
                        <div className="mb-3 font-medium text-white">Transfert des emails reçus</div>
                        <div className="mb-2 text-xs text-slate-400">Rediriger automatiquement les emails reçus vers ces adresses</div>
                        <div className="flex flex-col gap-2 sm:flex-row">
                          <Input value={newForward} onChange={(e) => setNewForward(e.target.value)} placeholder="email@exemple.com" className="w-full text-white bg-slate-900 border-slate-700" />
                          <Button className="w-full sm:w-auto" onClick={async () => {
                            const e = newForward.trim();
                            if (!e) return;
                            const nextCfg = { ...cfg, forwardReceivedTo: [...(cfg.forwardReceivedTo || []), e] };
                            await updateAndSave(nextCfg);
                            setNewForward('');
                          }}>
                            <Plus size={16} className="mr-2" />
                            Ajouter
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {(cfg.forwardReceivedTo || []).map((e, idx) => (
                            <Badge key={idx} className="px-2 py-1 bg-slate-700 text-slate-200 border-slate-600">
                              {e}
                              <button className="inline-flex items-center ml-2" onClick={async () => {
                                const nextCfg = { ...cfg, forwardReceivedTo: (cfg.forwardReceivedTo || []).filter(x => x !== e) };
                                await updateAndSave(nextCfg);
                              }}>
                                <Trash2 size={12} />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="models">
                      <div className="p-4 rounded-lg border border-slate-700">
                        <div className="flex justify-between items-center mb-3">
                          <div className="font-medium text-white">Modèles de contenu (composer)</div>
                          <Button variant="outline" className="border-slate-700" onClick={addNewTemplate}>
                            <Plus size={16} className="mr-2" />
                            Ajouter un modèle
                          </Button>
                        </div>
                        <div className="rounded-lg border border-slate-700 bg-slate-900/40">
                          {templatesState.length === 0 ? (
                            <p className="p-3 text-slate-400">Aucun modèle. Ajoutez-en un.</p>
                          ) : (
                            <Table className="text-slate-300">
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Libellé</TableHead>
                                  <TableHead>Identifiant</TableHead>
                                  <TableHead>Taille HTML</TableHead>
                                  <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {templatesState.map((t, idx) => (
                                  <TableRow key={t.id}>
                                    <TableCell className="text-white">{t.label}</TableCell>
                                    <TableCell className="text-slate-400">{t.id}</TableCell>
                                    <TableCell className="text-slate-400">{(t.html || '').length}</TableCell>
                                    <TableCell>
                                      <div className="flex gap-2 justify-end">
                                        <Button size="sm" variant="outline" className="border-slate-700" onClick={() => openEditTemplate(idx)}>Éditer</Button>
                                        <Button size="sm" variant="ghost" className="text-red-300 hover:text-red-400 hover:bg-red-500/10" onClick={() => removeTemplate(idx)}>
                                          <Trash2 size={16} />
                                        </Button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="events">
                      <div className="p-4 rounded-lg border border-slate-700">
                        <div className="mb-1 font-medium text-white">Gestion des événements</div>
                        <p className="text-sm text-slate-400">Base centrale des notifications et modèles liés aux événements.</p>
                        <div className="flex flex-col gap-3 mt-3">
                          <div className="flex justify-between items-center">
                            <div className="text-sm text-slate-400">Modèles de message</div>
                            <Button variant="outline" className="border-slate-700" onClick={async () => {
                              const baseSubj = cfg.subjectTemplate || 'Notification Vol.{volume}';
                              const baseBody = cfg.bodyTemplate || '<p>Bonjour {name},</p>';
                              const next = { ...(cfg || {}), models: [ ...(cfg?.models || []), { name: `Modèle ${((cfg?.models || []).length + 1)}`, enabled: true, trigger: { type: 'reservation_status', mode: 'on_change', toStatus: 'pending' }, subjectTemplate: baseSubj, bodyTemplate: baseBody } ] };
                              await updateAndSave(next);
                            }}>
                              <Plus size={16} className="mr-2" />
                              Ajouter un modèle
                            </Button>
                          </div>
                          <div className="rounded-lg border border-slate-700 bg-slate-900/40">
                            <Table className="text-slate-300">
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Nom</TableHead>
                                  <TableHead>Type</TableHead>
                                  <TableHead>Statut</TableHead>
                                  <TableHead>Actif</TableHead>
                                  <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {(cfg?.models || []).map((m, idx) => {
                                  const tType = (m?.trigger?.type || 'reservation_status');
                                  const status = m?.trigger?.toStatus || 'pending';
                                  return (
                                    <TableRow key={idx} className="hover:bg-slate-800/40">
                                      <TableCell className="text-white">{m?.name || `Modèle ${idx+1}`}</TableCell>
                                      <TableCell className="text-slate-300">{tType}</TableCell>
                                      <TableCell className="text-slate-300">{status}</TableCell>
                                      <TableCell>
                                        <div className="flex gap-2 items-center">
                                          <Switch
                                            checked={!!m.enabled}
                                            onCheckedChange={async (v) => {
                                              const next = { ...(cfg || {}) };
                                              next.models = [...(cfg?.models || [])];
                                              next.models[idx] = { ...m, enabled: v };
                                              await updateAndSave(next);
                                            }}
                                            className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-slate-600"
                                          />
                                          <Badge className={m.enabled ? "bg-green-600/20 text-green-300 border-green-600/30" : "bg-slate-700 text-slate-300 border-slate-600"}>
                                            {m.enabled ? "Actif" : "Inactif"}
                                          </Badge>
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex gap-2 justify-end">
                                          <Button size="sm" variant="outline" className="border-slate-700" onClick={() => openEditModel(idx)}>Éditer</Button>
                                          <Button size="sm" variant="ghost" className="text-red-300 hover:text-red-400 hover:bg-red-500/10" onClick={async () => {
                                            const next = { ...(cfg || {}) };
                                            next.models = (cfg?.models || []).filter((_, i) => i !== idx);
                                            await updateAndSave(next);
                                          }}>
                                            <Trash2 size={16} />
                                          </Button>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            </TabsContent>
            </Tabs>
            {editTplOpen && (
              <Dialog open={editTplOpen} onOpenChange={(o) => { if (!o) closeEditTemplate(); }}>
                <DialogContent className="max-w-4xl text-white bg-slate-950 border-slate-800">
                  <DialogHeader>
                    <DialogTitle>Éditer le modèle</DialogTitle>
                    <DialogDescription>Modifier le libellé et le contenu HTML. La prévisualisation s’actualise en direct.</DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Libellé</Label>
                        <Input
                          value={editTplLabel}
                          onChange={(e) => setEditTplLabel(e.target.value)}
                          placeholder="Nom du modèle"
                          className="text-white bg-slate-900 border-slate-700"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Contenu (HTML)</Label>
                        <textarea
                          value={editTplHtml}
                          onChange={(e) => setEditTplHtml(e.target.value)}
                          rows={12}
                          className="p-3 w-full text-white rounded border bg-slate-900 border-slate-700"
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" className="border-slate-700" onClick={closeEditTemplate}>Annuler</Button>
                        <Button className="text-black bg-white hover:bg-gray-200" onClick={saveEditTemplate}>Enregistrer</Button>
                      </div>
                    </div>
                    <div className="rounded border border-slate-800 h-[60vh] overflow-hidden bg-white">
                      <iframe
                        title="template-preview"
                        style={{ width: '100%', height: '100%', border: '0', background: 'white' }}
                        srcDoc={sanitizeForPreview(buildEmailHtml(editTplHtml || '<p>(Sans contenu)</p>', editTplLabel || 'Aperçu'))}
                        sandbox="allow-same-origin"
                      />
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            {editModelOpen && (
              <Dialog open={editModelOpen} onOpenChange={(o) => { if (!o) closeEditModel(); }}>
                <DialogContent className="max-w-5xl text-white bg-slate-950 border-slate-800">
                  <DialogHeader>
                    <DialogTitle>Éditer le modèle</DialogTitle>
                    <DialogDescription>Modifier les paramètres et le contenu. La prévisualisation s’actualise en direct.</DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-3">
                      <div className="flex gap-3 items-center">
                        <Input
                          value={editModelData.name}
                          onChange={(e) => setEditModelData(d => ({ ...d, name: e.target.value }))}
                          placeholder="Nom du modèle"
                          className="text-white bg-slate-900 border-slate-700"
                        />
                        <Switch
                          checked={!!editModelData.enabled}
                          onCheckedChange={(v) => setEditModelData(d => ({ ...d, enabled: v }))}
                          className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-slate-600"
                        />
                        <Badge className={editModelData.enabled ? "bg-green-600/20 text-green-300 border-green-600/30" : "bg-slate-700 text-slate-300 border-slate-600"}>
                          {editModelData.enabled ? "Actif" : "Inactif"}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label className="text-slate-300">Type</Label>
                          <Select
                            value={editModelData.trigger.type}
                            onValueChange={(v) => setEditModelData(d => ({ ...d, trigger: { ...d.trigger, type: v, toStatus: (v === 'email_received' ? 'received' : 'pending') } }))}
                          >
                            <SelectTrigger className="w-full text-white bg-slate-900 border-slate-700">
                              <SelectValue placeholder="Sélectionner le type" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-700">
                              <SelectItem value="reservation_status" className="text-white">Statut de réservation</SelectItem>
                              <SelectItem value="order_status" className="text-white">Statut de commande</SelectItem>
                              <SelectItem value="event_status" className="text-white">Statut d’événement</SelectItem>
                              <SelectItem value="email_received" className="text-white">Email reçu</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-slate-300">Statut cible</Label>
                          <Select
                            value={editModelData.trigger.toStatus}
                            onValueChange={(v) => setEditModelData(d => ({ ...d, trigger: { ...d.trigger, toStatus: v } }))}
                          >
                            <SelectTrigger className="w-full text-white bg-slate-900 border-slate-700">
                              <SelectValue placeholder="Sélectionner une valeur" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-700">
                              {(() => {
                                const t = editModelData.trigger.type;
                                const opts = t === 'order_status'
                                  ? [{ value: 'open', label: 'Ouvert' }, { value: 'sent', label: 'Envoyé à la cuisine' }, { value: 'paid', label: 'Payé' }, { value: 'cancelled', label: 'Annulé' }]
                                  : t === 'event_status'
                                  ? [{ value: 'upcoming', label: 'À venir' }, { value: 'available', label: 'Réservable' }, { value: 'sold_out', label: 'Complet' }]
                                  : t === 'email_received'
                                  ? [{ value: 'received', label: 'Reçu (tous)' }, { value: 'received_with_prefix', label: 'Reçu (par préfixe)' }]
                                  : [{ value: 'pending', label: 'En attente' }, { value: 'confirmed', label: 'Confirmé' }, { value: 'cancelled', label: 'Annulé' }];
                                return opts.map(o => <SelectItem key={o.value} value={o.value} className="text-white">{o.label}</SelectItem>);
                              })()}
                            </SelectContent>
                          </Select>
                          {editModelData.trigger.type === 'email_received' && editModelData.trigger.toStatus === 'received_with_prefix' && (
                            <div className="mt-2 space-y-2">
                              <Label className="text-slate-300">Préfixe</Label>
                              <Input
                                value={editModelData.trigger.prefix || ''}
                                onChange={(e) => setEditModelData(d => ({ ...d, trigger: { ...d.trigger, prefix: e.target.value } }))}
                                className="text-white bg-slate-900 border-slate-700"
                                placeholder="ex: contact, booking, info"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Sujet</Label>
                        <Input
                          value={editModelData.subjectTemplate}
                          onChange={(e) => setEditModelData(d => ({ ...d, subjectTemplate: e.target.value }))}
                          className="text-white bg-slate-900 border-slate-700"
                        />
                        <p className="text-xs text-slate-500">Variables: {'{volume} {theme} {name} {guests} {price} {date} {time} {status}'}</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Contenu (HTML)</Label>
                        <textarea
                          value={editModelData.bodyTemplate}
                          onChange={(e) => setEditModelData(d => ({ ...d, bodyTemplate: e.target.value }))}
                          rows={10}
                          className="p-3 w-full text-white rounded border bg-slate-900 border-slate-700"
                        />
                        <p className="text-xs text-slate-500">Variables: {'{volume} {theme} {name} {guests} {price} {date} {time} {status}'}</p>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" className="border-slate-700" onClick={closeEditModel}>Annuler</Button>
                        <Button className="text-black bg-white hover:bg-gray-200" onClick={saveEditModel}>Enregistrer</Button>
                      </div>
                    </div>
                    <div className="rounded border border-slate-800 h-[60vh] overflow-hidden bg-white">
                      <iframe
                        title="model-preview"
                        style={{ width: '100%', height: '100%', border: '0', background: 'white' }}
                        srcDoc={sanitizeForPreview(buildEmailHtml(editModelData.bodyTemplate || '<p>(Sans contenu)</p>', editModelData.subjectTemplate || 'Aperçu'))}
                        sandbox="allow-same-origin"
                      />
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
    </div>
  );
};

export default ModernNotificationsTab;
